import { sessionId, visitorId } from "./ids";
import { clip, closestCanvas, closestTrackable, cssPath, isSensitive, textOf } from "./describe";

export type BehaviorEvent = {
  occurredAt: string;
  category: string;
  action: string;
  label?: string;
  path?: string;
  title?: string;
  referrer?: string;
  targetId?: string;
  targetText?: string;
  targetHref?: string;
  targetTag?: string;
  props?: Record<string, unknown>;
  viewportW?: number;
  viewportH?: number;
  locale?: string;
  timezone?: string;
  userId?: number;
  userName?: string;
  userPlan?: string;
};

type Identity = { userId?: number; userName?: string; userPlan?: string };

const queue: BehaviorEvent[] = [];
let flushTimer = 0;
let identity: Identity = {};
let started = false;
let lastPath = "";
let enteredAt = 0;
let maxScroll = 0;
let lastClickKey = "";
let lastClickAt = 0;
let pointer: { x: number; y: number } | null = null;

export function setBehaviorIdentity(next: Identity) {
  identity = next;
}

export function track(category: string, action: string, label?: string, props?: Record<string, unknown>) {
  enqueue({
    occurredAt: new Date().toISOString(),
    category,
    action,
    label,
    path: window.location.pathname + window.location.search,
    title: document.title,
    referrer: document.referrer || undefined,
    props,
    ...viewport(),
    ...identity,
  });
}

export function startBehaviorTracker() {
  if (started || typeof window === "undefined")
    return () => undefined;
  started = true;
  lastPath = window.location.pathname + window.location.search;
  enteredAt = Date.now();
  track("page", "view", pageLabel(lastPath), { referrer: document.referrer || undefined });

  const onClick = (event: MouseEvent) => captureClick(event);
  const onPointerDown = (event: PointerEvent) => {
    pointer = { x: event.clientX, y: event.clientY };
  };
  const onSubmit = (event: Event) => captureSubmit(event);
  const onChange = (event: Event) => captureChange(event);
  const onScroll = () => {
    const root = document.documentElement;
    const height = Math.max(root.scrollHeight - window.innerHeight, 1);
    maxScroll = Math.max(maxScroll, Math.round((window.scrollY / height) * 100));
  };
  const onHide = () => {
    leavePage("hidden");
    void flush(true);
  };
  const onError = (event: ErrorEvent) => {
    track("error", "js", clip(event.message || "脚本错误", 200), {
      file: event.filename,
      line: event.lineno,
      col: event.colno,
    });
  };
  const onReject = (event: PromiseRejectionEvent) => {
    const reason = event.reason instanceof Error ? event.reason.message : String(event.reason || "未处理的 Promise");
    track("error", "promise", clip(reason, 200));
  };

  document.addEventListener("click", onClick, true);
  document.addEventListener("pointerdown", onPointerDown, true);
  document.addEventListener("submit", onSubmit, true);
  document.addEventListener("change", onChange, true);
  window.addEventListener("scroll", onScroll, { passive: true });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden")
      onHide();
    else
      track("visibility", "visible", pageLabel(lastPath));
  });
  window.addEventListener("pagehide", onHide);
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onReject);

  return () => {
    started = false;
    document.removeEventListener("click", onClick, true);
    document.removeEventListener("pointerdown", onPointerDown, true);
    document.removeEventListener("submit", onSubmit, true);
    document.removeEventListener("change", onChange, true);
    window.removeEventListener("scroll", onScroll);
    window.removeEventListener("pagehide", onHide);
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onReject);
  };
}

export function trackPageChange(path: string) {
  if (path === lastPath)
    return;
  leavePage("navigate");
  lastPath = path;
  enteredAt = Date.now();
  maxScroll = 0;
  track("page", "view", pageLabel(path));
}

function leavePage(reason: string) {
  if (!lastPath)
    return;
  track("page", "leave", pageLabel(lastPath), {
    durationMs: Date.now() - enteredAt,
    scrollMax: maxScroll,
    reason,
  });
}

function captureClick(event: MouseEvent) {
  const dragged = pointer
    ? Math.hypot(event.clientX - pointer.x, event.clientY - pointer.y) > 8
    : false;
  const canvas = closestCanvas(event.target);
  if (canvas && window.location.pathname.startsWith("/moodboard")) {
    if (dragged)
      return;
    rememberClick("moodboard:canvas");
    track("moodboard", "canvas", "点选 3D 画面");
    return;
  }
  const node = closestTrackable(event.target);
  if (!node || isSensitive(node))
    return;
  const href = node instanceof HTMLAnchorElement ? node.getAttribute("href") || undefined : undefined;
  const label = textOf(node) || node.getAttribute("aria-label") || node.tagName.toLowerCase();
  if (!rememberClick(`${clickAction(node, href)}:${label}`))
    return;
  enqueue({
    occurredAt: new Date().toISOString(),
    category: window.location.pathname.startsWith("/moodboard") ? "moodboard" : "click",
    action: clickAction(node, href),
    label,
    path: window.location.pathname + window.location.search,
    title: document.title,
    referrer: document.referrer || undefined,
    targetId: node.id || node.getAttribute("data-track") || node.getAttribute("aria-label") || undefined,
    targetText: label,
    targetHref: href,
    targetTag: node.tagName.toLowerCase(),
    props: {
      selector: cssPath(node),
      x: Math.round(event.clientX),
      y: Math.round(event.clientY),
    },
    ...viewport(),
    ...identity,
  }, true);
}

function rememberClick(key: string) {
  const now = Date.now();
  if (key === lastClickKey && now - lastClickAt < 280)
    return false;
  lastClickKey = key;
  lastClickAt = now;
  return true;
}

function captureSubmit(event: Event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || isSensitive(form))
    return;
  track("form", "submit", form.getAttribute("aria-label") || form.name || "表单", {
    selector: cssPath(form),
    fields: [...form.elements].filter((item) => item instanceof HTMLInputElement || item instanceof HTMLSelectElement || item instanceof HTMLTextAreaElement).length,
  });
}

function captureChange(event: Event) {
  const node = event.target;
  if (!(node instanceof HTMLSelectElement) && !(node instanceof HTMLInputElement))
    return;
  if (isSensitive(node))
    return;
  if (node instanceof HTMLInputElement && !["search", "checkbox", "radio"].includes(node.type))
    return;
  const value = node instanceof HTMLInputElement && (node.type === "checkbox" || node.type === "radio")
    ? String(node.checked)
    : clip(node.value || "", 80);
  const placeholder = node instanceof HTMLInputElement ? node.placeholder : "";
  track(node instanceof HTMLSelectElement ? "form" : "search", "change", textOf(node) || node.name || placeholder || "筛选", {
    value,
    selector: cssPath(node),
  });
}

function clickAction(node: Element, href?: string) {
  if (node.getAttribute("data-track"))
    return node.getAttribute("data-track") || "click";
  if (href)
    return href.startsWith("http") ? "outbound" : "link";
  if (node.tagName === "BUTTON" || node.getAttribute("role") === "button")
    return "button";
  return "click";
}

function pageLabel(path: string) {
  if (path === "/")
    return "首页";
  if (path.startsWith("/moodboard"))
    return "情绪板";
  if (path.startsWith("/library"))
    return "素材库";
  if (path.startsWith("/community"))
    return "社区";
  if (path.startsWith("/workbench"))
    return "工作台";
  if (path.startsWith("/login"))
    return "登录";
  if (path.startsWith("/register"))
    return "注册";
  if (path.startsWith("/site/"))
    return "站点页";
  return path;
}

function viewport() {
  return {
    viewportW: window.innerWidth,
    viewportH: window.innerHeight,
    locale: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}

function enqueue(event: BehaviorEvent, immediate = false) {
  queue.push(event);
  const rush = immediate || event.category === "moodboard" || event.category === "click";
  if (rush || queue.length >= 4) {
    window.clearTimeout(flushTimer);
    void flush(false);
    return;
  }
  window.clearTimeout(flushTimer);
  flushTimer = window.setTimeout(() => {
    void flush(false);
  }, 800);
}

async function flush(keepalive: boolean) {
  if (queue.length === 0)
    return;
  const events = queue.splice(0, 40);
  const body = JSON.stringify({
    sessionId: sessionId(),
    visitorId: visitorId(),
    events,
  });
  const token = localStorage.getItem("client_token");
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token)
    headers.Authorization = `Bearer ${token}`;
  try {
    const response = await fetch("/api/client/behavior/events", {
      method: "POST",
      headers,
      body,
      keepalive,
    });
    if (!response.ok && !keepalive)
      queue.unshift(...events);
  }
  catch {
    if (!keepalive)
      queue.unshift(...events);
  }
}
